import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { formatApiError, isAuthError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Building2, Users, Search, ChevronRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

export default function BusinessesPage() {
  useDocumentTitle("Businesses", "Group learners by external company.");
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = currentUser?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const empty = { name: "", contact_name: "", contact_email: "", contact_phone: "", address: "", website: "", notes: "" };
  const [form, setForm] = useState(empty);

  const fetchList = useCallback(async () => {
    try {
      const { data } = await api.get("/businesses");
      setList(data);
    } catch (err) { if (!isAuthError(err)) toast.error("Failed to load businesses"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openCreate = () => { setEditing(null); setForm(empty); setDialogOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({
      name: b.name || "", contact_name: b.contact_name || "", contact_email: b.contact_email || "",
      contact_phone: b.contact_phone || "", address: b.address || "", website: b.website || "", notes: b.notes || "",
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/businesses/${editing.id}`, form);
        toast.success("Business updated");
      } else {
        await api.post("/businesses", form);
        toast.success("Business created");
      }
      setDialogOpen(false); fetchList();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/businesses/${deleting.id}`);
      toast.success("Business deleted"); setDeleteOpen(false); fetchList();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDetail = (b) => { navigate(`/businesses/${b.id}`); };

  const filtered = list.filter(b =>
    !search || b.name.toLowerCase().includes(search.toLowerCase()) ||
    (b.contact_email || "").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4" data-testid="businesses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Building2 size={16} className="text-muted-foreground" />
          <span className="font-heading font-semibold text-sm">Businesses</span>
          <span className="text-xs text-muted-foreground">{list.length}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search businesses…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs w-56"
              data-testid="business-search-input"
            />
          </div>
          <Button size="sm" onClick={openCreate} className="bg-slate-900 hover:bg-slate-800 text-white h-8 text-xs" data-testid="business-create-btn">
            <Plus size={14} className="mr-1" /> Add Business
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Name</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Contact</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Learners</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs">Certificate Status</th>
              <th className="px-4 py-2.5 font-medium text-muted-foreground text-xs text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground text-sm">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground text-sm">
                {list.length === 0 ? "No businesses yet — click 'Add Business' to create your first one." : "No businesses match your search."}
              </td></tr>
            ) : filtered.map((b) => (
              <tr key={b.id} className="border-t hover:bg-slate-50 cursor-pointer" onClick={() => openDetail(b)} data-testid={`business-row-${b.id}`}>
                <td className="px-4 py-2.5 font-medium">{b.name}</td>
                <td className="px-4 py-2.5 text-muted-foreground text-xs">
                  {b.contact_name && <div>{b.contact_name}</div>}
                  {b.contact_email && <div>{b.contact_email}</div>}
                  {b.contact_phone && <div>{b.contact_phone}</div>}
                  {!b.contact_name && !b.contact_email && !b.contact_phone && <span className="text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-1 text-xs">
                    <Users size={12} className="text-muted-foreground" />
                    {b.learner_count}
                  </span>
                </td>
                <td className="px-4 py-2.5 space-x-1">
                  {b.expired > 0 && <Badge className="bg-rose-100 text-rose-700 border-rose-200 text-[10px]">{b.expired} expired</Badge>}
                  {b.expiring_soon > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">{b.expiring_soon} expiring</Badge>}
                  {b.active > 0 && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">{b.active} active</Badge>}
                  {b.expired === 0 && b.expiring_soon === 0 && b.active === 0 && <span className="text-xs text-slate-300">—</span>}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(b)} data-testid={`business-edit-${b.id}`}>
                    <Pencil size={14} />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700" onClick={() => { setDeleting(b); setDeleteOpen(true); }} data-testid={`business-delete-${b.id}`}>
                      <Trash2 size={14} />
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openDetail(b)}>
                    <ChevronRight size={14} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="business-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Business" : "Add Business"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="business-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Contact Name</Label><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="business-contact-name-input" /></div>
              <div><Label className="text-xs">Contact Phone</Label><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} data-testid="business-contact-phone-input" /></div>
            </div>
            <div><Label className="text-xs">Contact Email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="business-contact-email-input" /></div>
            <div><Label className="text-xs">Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="business-address-input" /></div>
            <div><Label className="text-xs">Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" data-testid="business-website-input" /></div>
            <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="business-notes-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="business-submit-btn">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Delete business?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will detach any learners linked to <span className="font-medium text-slate-700">{deleting?.name}</span> (the learners themselves will not be deleted).
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)}>Cancel</Button>
            <Button onClick={handleDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="business-delete-confirm-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
